import { useQuery, UseQueryResult } from '@tanstack/react-query'
import { gql } from 'graphql-request'

import { useGraphQLClient } from '@/hooks/useGraphQLClient'

const THIRTY_MINUTES = 1000 * 60 * 30

const GET_FACILITY_QUERY = gql`
  query GetFacility($id: String!) {
    getFacility(id: $id) {
      id
      name
      partner
      formattedAddress
      iconUrl
      shipToAddress {
        address1
        address2
        city
        state
        zip5
        countryCode
      }
    }
  }
`

interface ShipToAddress {
  address1: string
  address2?: string
  city: string
  state: string
  zip5: string
  countryCode: string
}

export interface Facility {
  id: string
  name: string
  partner: boolean
  formattedAddress: string
  iconUrl?: string
  shipToAddress?: ShipToAddress
}

interface GetFacilityQueryResponse {
  getFacility: Facility
}

export const useGetFacilityApi = (
  facilityId: string | undefined,
): UseQueryResult<Facility, Error> => {
  const client = useGraphQLClient()

  const fetchFacility = async (facilityId: string | undefined): Promise<Facility> => {
    const data = await client.request<GetFacilityQueryResponse>(GET_FACILITY_QUERY, {
      id: facilityId,
    })

    return data.getFacility
  }

  return useQuery<Facility, Error>({
    queryKey: [facilityId],
    enabled: () => !!facilityId,
    queryFn: () => fetchFacility(facilityId),
    staleTime: THIRTY_MINUTES,
    gcTime: THIRTY_MINUTES,
  })
}
